import { useModal } from "@/features/modal/ModalStackManager";
import styled from "@emotion/styled";
import { Box, Button, Select, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";

import { ItemAutoComplete } from "@/features/ui/autoComplete/item/item-autoComplete";
import { OperationAutoComplete } from "@/features/ui/autoComplete/operation/operation-autoComplete";
import { EquipmentsMultiAutoComplete } from "@/features/ui/multiAutoComplete/equipment/equipment-multiAutoComplete";

import { mutateRoutings } from "@/api/routing/useRoutingQuery";
import { consumeType, routingType } from "@/constants/routings";
import customAlert from "@/features/ui/alert/alert";
import { MoldAutoComplete } from "@/features/ui/autoComplete/mold/mold-autoComplete";
import { handleErrorResponse } from "@/utils/errorMessage";
import { validateFormValues } from "@/utils/validation";
import { DefaultApiRoutingsRoutingIdPutRequest } from "@sizlcorp/sizl-api-document/dist/models";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

export const RoutingEditForm = (params: { formatterProps: any }) => {
  const { t } = useTranslation();
  const { closeModal } = useModal();
  const { formatterProps } = params;
  const queryClient = useQueryClient();
  const { mutate: updateMutate } = useMutation(
    (params: DefaultApiRoutingsRoutingIdPutRequest) =>
      mutateRoutings
        .update(params)
        .mutationFn(params as DefaultApiRoutingsRoutingIdPutRequest | any),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["routings"]);
        closeModal(formatterProps);
        customAlert(
          `${t("message.editSuccess", { item: `${t("routing.routing")}` })}`,
          `${t("common.editSuccess")}`,
          "green",
        );
      },
      onError: (error: any) => {
        customAlert(
          handleErrorResponse({ error, errorContext: t("routing.code") }),
          `${t("common.editFail", { item: `${t("routing.routing")}` })}`,
          "red",
        );
      },
    },
  );

  const lnRoutingType = routingType.map((item: any) => ({
    ...item,
    label: t(item.label),
  }));

  const lnConsumeType = consumeType.map((item: any) => ({
    ...item,
    label: t(item.label),
  }));

  const form = useForm({
    initialValues: {
      itemCode: formatterProps.itemCode,
      code: formatterProps.code,
      routingType: formatterProps.routingType,
      moldCode: formatterProps.moldCode,
      consumeType: formatterProps.consumeType,
      operationCode: formatterProps.operationCode,
      seq: formatterProps.seq,
      equipmentCode: formatterProps.equipmentCode,
    },
  });

  const onSubmit = async () => {
    try {
      updateMutate({
        routingId: formatterProps?.id,
        routingsGetRequest: {
          itemCode: form.values.itemCode ?? (formatterProps.itemCode as any),
          code: form.values.code ?? (formatterProps.code as any),
          routingType: form.values.routingType ?? (formatterProps.routingType as any),
          moldCode: form.values.moldCode ?? (formatterProps.moldCode as any),
          consumeType: form.values.consumeType ?? (formatterProps.consumeType as any),
          operationCode: form.values.operationCode ?? (formatterProps.operationCode as any),
          seq: form.values.seq ?? (formatterProps.seq as any),
          equipmentCode: form.values.equipmentCode ?? (formatterProps.equipmentCode as any),
        },
      });
    } catch (error) {
      customAlert(
        handleErrorResponse({ error, errorContext: t("routing.code") }),
        `${t("common.editFail", { item: `${t("routing.routing")}` })}`,
        "red",
      );
    }
  };

  return (
    <Box w={420} mx="auto">
      <Form>
        <ModalFieldSet>
          <ItemAutoComplete
            withAsterisk
            label={t("item.code")}
            placeholder={t("placeholder.input", { item: `${t("item.code")}` })}
            defaultValue={formatterProps?.itemCode}
            {...form.getInputProps("itemCode")}
            error={form.isDirty("itemCode") ? t("common.toSave") : ""}
          />
          <OperationAutoComplete
            withAsterisk
            label={t("operation.code")}
            placeholder={t("placeholder.select", { item: `${t("operation.code")}` })}
            defaultValue={formatterProps?.operationCode}
            {...form.getInputProps("operationCode")}
            error={form.isDirty("operationCode") ? t("common.toSave") : ""}
          />
          <TextInput
            withAsterisk
            label={t("routing.code")}
            placeholder={t("placeholder.input", { item: `${t("routing.code")}` })}
            defaultValue={formatterProps?.code}
            {...form.getInputProps("code")}
            error={form.isDirty("code") ? t("common.toSave") : ""}
            styles={{
              input: {
                color: form.isDirty("code") ? "red !important" : "",
                borderColor: form.isDirty("code") ? "red !important" : "",
              },
              error: { fontSize: 11, color: "red" },
            }}
          />
          <Select
            withAsterisk
            label={t("routing.type")}
            placeholder={t("placeholder.select", { item: `${t("routing.type")}` })}
            defaultValue={formatterProps?.routingType}
            data={lnRoutingType}
            {...form.getInputProps("routingType")}
            error={form.isDirty("routingType") ? t("common.toSave") : ""}
            styles={{
              input: {
                color: form.isDirty("routingType") ? "red !important" : "",
                borderColor: form.isDirty("routingType") ? "red !important" : "",
              },
              error: { fontSize: 11, color: "red" },
            }}
          />
          <MoldAutoComplete
            label={t("mold.code")}
            placeholder={t("placeholder.select", { item: `${t("mold.code")}` })}
            defaultValue={formatterProps?.moldCode}
            {...form.getInputProps("moldCode")}
            error={form.isDirty("moldCode") ? t("common.toSave") : ""}
          />
          <Select
            withAsterisk
            placeholder={t("placeholder.select", { item: `${t("routing.consumeType")}` })}
            label={t("routing.consumeType")}
            defaultValue={formatterProps?.consumeType}
            data={lnConsumeType}
            {...form.getInputProps("consumeType")}
            error={form.isDirty("consumeType") ? t("common.toSave") : ""}
            styles={{
              input: {
                color: form.isDirty("consumeType") ? "red !important" : "inherit",
                borderColor: form.isDirty("consumeType") ? "red !important" : "",
              },
              error: { fontSize: 11, color: "red" },
            }}
          />
          <TextInput
            withAsterisk
            label={t("routing.seq")}
            placeholder={t("placeholder.input", { item: `${t("routing.seq")}` })}
            defaultValue={formatterProps?.seq}
            {...form.getInputProps("seq")}
            error={form.isDirty("seq") ? t("common.toSave") : ""}
            styles={{
              input: {
                color: form.isDirty("seq") ? "red !important" : "",
                borderColor: form.isDirty("seq") ? "red !important" : "",
              },
              error: { fontSize: 11, color: "red" },
            }}
          />
          <EquipmentsMultiAutoComplete label="설비 코드" {...form.getInputProps("equipmentCode")} />
          <ModalConfirmBtnBox>
            <Button color="gray" onClick={closeModal}>
              {t("common.cancel")}
            </Button>
            <Button
              disabled={validateFormValues({
                pageType: "routing",
                values: form.values,
              })}
              onClick={onSubmit}
            >
              {t("common.edit", { item: `${t("routing.routing")}` })}
            </Button>
          </ModalConfirmBtnBox>
        </ModalFieldSet>
      </Form>
    </Box>
  );
};

const Form = styled.form`
  width: 100%;
  height: 100%;
`;

const ModalFieldSet = styled.fieldset`
  border: none;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ModalConfirmBtnBox = styled.div`
  padding: 24px 0;
  display: flex;
  justify-content: flex-end;
  gap: 20px;
`;
